import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowUpRight, CheckCircle2, Loader2 } from "lucide-react";
import { Reveal, MaskLines, Eyebrow } from "./motion";

const API = process.env.REACT_APP_BACKEND_URL;
const EMPTY = { name: "", email: "", phone: "", city: "", interest: "conhecer", message: "" };
const OPTIONS = [["conhecer", "Conhecer a oportunidade"]];

const Field = ({ label, id, ...p }) => (
  <label className="block">
    <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-slate-300">{label}</span>
    <input id={id} data-testid={`lead-${id}-input`} {...p} className="mt-2 w-full border-b border-white/25 bg-transparent py-3 text-lg text-white outline-none transition-[border-color] duration-300 placeholder:text-slate-500 focus:border-orange" />
  </label>
);

const LeadForm = () => {
  const [f, setF] = useState(EMPTY);
  const [state, setState] = useState("idle");
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  useEffect(() => {
    const on = (e) => setF((p) => ({ ...p, interest: e.detail }));
    window.addEventListener("si:interest", on);
    return () => window.removeEventListener("si:interest", on);
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setState("loading");
    try {
      await axios.post(`${API}/api/leads`, f);
      setState("done");
      toast.success("Pedido recebido. Entraremos em contacto em breve.");
    } catch (err) {
      setState("idle");
      toast.error(err?.response?.status === 422 ? "Verifique os dados preenchidos." : "Não foi possível enviar. Tente novamente.");
    }
  };

  if (state === "done") {
    return (
      <div data-testid="lead-success" className="flex min-h-[420px] flex-col justify-center rounded-[28px] border border-white/15 bg-white/[0.04] p-10">
        <CheckCircle2 className="h-12 w-12 text-orange" />
        <p className="mt-6 font-display text-3xl font-black">Obrigado, {f.name.split(" ")[0]}.</p>
        <p className="mt-3 text-slate-300">Recebemos o seu pedido. Vamos entrar em contacto para percebermos juntos o seu próximo passo.</p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} data-testid="contact-form" className="space-y-7 rounded-[28px] border border-white/15 bg-white/[0.04] p-7 backdrop-blur-xl sm:p-10">
      <div className="flex flex-wrap gap-2" role="radiogroup">
        {OPTIONS.map(([v, l]) => (
          <button type="button" key={v} onClick={() => setF({ ...f, interest: v })} data-testid={`lead-interest-${v}`} className={`rounded-full px-4 py-2 text-sm font-bold transition-[background-color,color] duration-300 ${f.interest === v ? "bg-orange text-white" : "bg-white/10 text-slate-200 hover:bg-white/20"}`}>
            {l}
          </button>
        ))}
      </div>
      <Field label="Nome" id="name" required minLength={2} value={f.name} onChange={set("name")} placeholder="O seu nome" />
      <div className="grid gap-7 sm:grid-cols-2">
        <Field label="Email" id="email" type="email" required value={f.email} onChange={set("email")} placeholder="nome@email.pt" />
        <Field label="Telefone" id="phone" type="tel" required minLength={6} value={f.phone} onChange={set("phone")} placeholder="+351 9XX XXX XXX" />
      </div>
      <Field label="Cidade / zona de interesse" id="city" value={f.city} onChange={set("city")} placeholder="Ex.: Porto" />
      <label className="block">
        <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-slate-300">Mensagem (opcional)</span>
        <textarea data-testid="lead-message-input" rows={3} value={f.message} onChange={set("message")} placeholder="Conte-nos um pouco sobre si" className="mt-2 w-full resize-none border-b border-white/25 bg-transparent py-3 text-lg text-white outline-none transition-[border-color] duration-300 placeholder:text-slate-500 focus:border-orange" />
      </label>
      <button disabled={state === "loading"} data-testid="contact-form-submit" className="group flex w-full items-center justify-center gap-3 rounded-full bg-orange py-5 font-display text-lg font-black uppercase tracking-wide text-white transition-[background-color,transform] duration-300 hover:-translate-y-1 hover:bg-orange-deep disabled:opacity-70">
        {state === "loading" ? <Loader2 className="h-5 w-5 animate-spin" /> : <>Conhecer Oportunidade <ArrowUpRight className="h-5 w-5 transition-transform duration-300 group-hover:rotate-45" /></>}
      </button>
    </form>
  );
};

export const Contact = () => (
  <section id="contacto" className="grain relative overflow-hidden bg-navy py-24 text-white sm:py-36">
    <div className="spot pointer-events-none absolute -left-40 bottom-0 h-[700px] w-[700px]" />
    <div className="relative mx-auto grid max-w-[1400px] gap-16 px-5 sm:px-8 lg:grid-cols-12">
      <div className="lg:col-span-6">
        <Eyebrow dark num="10">O próximo passo</Eyebrow>
        <h2 className="mt-6 font-display text-4xl font-black leading-[1.02] tracking-tight sm:text-5xl lg:text-6xl">
          <MaskLines lines={["Talvez esteja", "exatamente onde", <>eu estava em <span className="text-orange">1997.</span></>]} />
        </h2>
        <Reveal className="mt-10 space-y-2 font-display text-xl font-bold text-slate-200 sm:text-2xl">
          <p>Com experiência.</p><p>Com potencial.</p><p>Com vontade de construir algo maior.</p>
        </Reveal>
        <Reveal className="mt-8 max-w-md space-y-4 text-base leading-relaxed text-slate-300 sm:text-lg">
          <p>Mas sem saber qual é o próximo passo.</p>
          <p><strong className="text-white">Fale connosco.</strong> Vamos perceber juntos se a Soluções Ideais pode fazer parte da sua história.</p>
        </Reveal>
      </div>
      <Reveal className="lg:col-span-6" delay={0.15}><LeadForm /></Reveal>
    </div>
    <div className="relative mx-auto mt-24 max-w-[1400px] border-t border-white/15 px-5 pt-16 text-center sm:px-8" data-testid="contact-closing-headline">
      <p className="font-display text-3xl font-black uppercase leading-[1.05] tracking-tight text-white sm:text-5xl lg:text-6xl">
        <MaskLines lines={["Mais do que construir negócios,", <><span className="text-orange">transformamos vidas</span> através do empreendedorismo.</>]} />
      </p>
    </div>
  </section>
);
